import React, { FC, useEffect, useState } from "react";
import { Routes } from "@Libs/Router/Components/Routes/Routes";
import { Route } from "@Libs/Router/Components/Route/Route";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { useParams } from "@Libs/Router/hooks/useParams";
import { Match, On, Other } from "@Libs/Utils/Components/Match/Match";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useMetadata } from "@Libs/Events/hooks/useMetadata";
import { useServiceOffer } from "@modules/ServiceAssignment/hooks/useServiceOffer";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";
import { loadBasketByItem } from "@modules/Purchasing/services/baskets";
import { ServiceOfferClosureCheckoutFlow } from "@modules/ServiceProcessing/Containers/ServiceOfferClosureFlow/ServiceOfferClosureCheckoutFlow";
import { ServiceOfferClosureSubscriptionFlow } from "@modules/Subscriptions/Containers/ServiceOfferClosureSubscriptionFlow/ServiceOfferClosureSubscriptionFlow";

export const ServiceOfferClosureFlowRouter: FC = () => {
  const { serviceOfferId } = useParams(["SERVICE_OFFER"]);
  const [serviceBasketId, setServiceBasketId] = useState<string | undefined>(
    undefined
  );
  const [hasAttemptedBasketLoad, setHasAttemptedBasketLoad] = useState(false);
  const {
    setMetadata,
    metadata: { context },
  } = useMetadata();

  useEffect(() => {
    setMetadata({ context: "serviceClosureFlow" });
  }, []);

  useEffect(() => {
    loadBasketForService(serviceOfferId);
  }, [serviceOfferId]);

  const loadBasketForService = async (serviceOfferId) => {
    try {
      const basket = await loadBasketByItem({
        itemId: Number(serviceOfferId),
        itemType: "service",
        quantity: 1,
      });
      setServiceBasketId(basket?.id);
    } catch (error) {
      console.log(error);
    } finally {
      setHasAttemptedBasketLoad(true);
    }
  };

  const isContextSet = context === "serviceClosureFlow";

  const {
    apiError: serviceOfferLoadingError,
    isLoading: isServiceOfferLoading,
  } = useServiceOffer(serviceOfferId);

  const { isLoading: isCurrentUserLoading } = useCurrentUser({});

  if (
    isServiceOfferLoading ||
    isCurrentUserLoading ||
    !isContextSet ||
    !serviceOfferId ||
    !hasAttemptedBasketLoad
  ) {
    return <LoadingScreen />;
  }

  return (
    <Match value={serviceOfferLoadingError?.response?.status}>
      <On valueBetween={[400, 499]}>
        <Navigate to="NOT_FOUND" reload />
      </On>

      <On valueBetween={[500, 599]}>
        <Navigate to="SERVER_ERROR" reload />
      </On>

      <Other>
        <Routes>
          <Route
            fragments="SUBSCRIBE"
            matchAny
            element={<ServiceOfferClosureSubscriptionFlow />}
          />
          <Route
            fragments={Checkout.root}
            matchAny
            element={
              <ServiceOfferClosureCheckoutFlow basketId={serviceBasketId} />
            }
          />
          <Route
            fragments="ROOT"
            matchAny
            element={
              <Navigate
                replace
                to={["SERVICE_OFFERS", "SERVICE_OFFER", "CLOSE", "CHECKOUT"]}
                params={{ serviceOfferId }}
              />
            }
          />
        </Routes>
      </Other>
    </Match>
  );
};
